import { isLibraryAssetId } from '../data/library-asset-ids.js';
import type { DataLayerProvenance, DataLayerPayload } from './data-layer.js';
import type { SchemaInput } from './schema-input.js';
import { isNumber, isSchemaRecord, isString } from './runtime-values.js';
import type { Stroke } from '../types/app.js';

export const DEFAULT_DRAWING_LAYER_ID = 'default';
export const FREE_DRAWING_LAYER_LIMIT = 5;
export const MAX_DRAWING_LAYERS = 100;
export const FREE_DATA_LAYER_LIMIT = 1;
export const FREE_DATA_LAYER_FEATURE_LIMIT = 1_000;

export interface DrawingLayer {
  id: string;
  group?: string[];
  locked?: boolean;
  name: string;
  visible: boolean;
  position: number;
}

export interface DrawingLayerMetadata extends DrawingLayer {
  drawingCount: number;
  active: boolean;
}

export const DEFAULT_DRAWING_LAYER: DrawingLayer = {
  id: DEFAULT_DRAWING_LAYER_ID,
  name: 'Drawings',
  visible: true,
  position: 0,
};

export function drawingLayerId(value: SchemaInput): string | null {
  return isString(value) && /^[a-z0-9_-]{1,64}$/i.test(value) ? value : null;
}

export function customDrawingLayerId(value: SchemaInput): string | undefined {
  const id = drawingLayerId(value);
  return id && id !== DEFAULT_DRAWING_LAYER_ID ? id : undefined;
}

function drawingLayerName(value: SchemaInput): string | null {
  if (!isString(value)) return null;
  const name = value.trim();
  return name ? name.slice(0, 120) : null;
}

export function normalizeDrawingLayers(value: SchemaInput): DrawingLayer[] {
  const source = Array.isArray(value) ? value.slice(0, MAX_DRAWING_LAYERS) : [];
  const seen = new Set<string>();
  const layers: DrawingLayer[] = [];
  for (const entry of source) {
    if (!isSchemaRecord(entry)) continue;
    const id = drawingLayerId(entry.id ?? entry.i);
    const name = drawingLayerName(entry.name ?? entry.n);
    if (!id || !name || seen.has(id)) continue;
    const rawPosition = entry.position ?? entry.p;
    const layer: DrawingLayer = {
      id,
      name,
      visible: entry.visible === false || entry.v === 0 ? false : true,
      position: isNumber(rawPosition) && Number.isFinite(rawPosition) ? rawPosition : layers.length,
    };
    if (Array.isArray(entry.group)) layer.group = entry.group.filter(isString).map(value => value.trim().slice(0, 80)).filter(Boolean).slice(0, 6);
    if (entry.locked === true) layer.locked = true;
    layers.push(layer);
    seen.add(id);
  }
  if (!seen.has(DEFAULT_DRAWING_LAYER_ID)) layers.unshift({ ...DEFAULT_DRAWING_LAYER });
  return layers
    .sort((left, right) => left.position - right.position)
    .map((layer, position) => ({ ...layer, position }));
}

export function strokeDrawingLayerId(stroke: Pick<Stroke, 'layerId'>): string {
  return drawingLayerId(stroke.layerId) ?? DEFAULT_DRAWING_LAYER_ID;
}

export function isStrokeVisible(stroke: Pick<Stroke, 'layerId'>, layers: DrawingLayer[]): boolean {
  const id = strokeDrawingLayerId(stroke);
  return layers.find((layer) => layer.id === id)?.visible ?? id === DEFAULT_DRAWING_LAYER_ID;
}

export function visibleStrokes(strokes: Stroke[], layers: DrawingLayer[]): Stroke[] {
  return strokes.filter((stroke) => isStrokeVisible(stroke, layers));
}

export function assignDrawingLayer(strokes: Stroke[], activeId: string): void {
  const layerId = customDrawingLayerId(activeId);
  for (const stroke of strokes) stroke.layerId = layerId;
}

export function visibleStrokesInLayerOrder(strokes: Stroke[], layers: DrawingLayer[]): Stroke[] {
  const order = new Map(layers.map((layer, index) => [layer.id, index]));
  return strokes
    .map((stroke, index) => ({ stroke, index, layer: order.get(strokeDrawingLayerId(stroke)) ?? 0 }))
    .filter(({ stroke }) => isStrokeVisible(stroke, layers))
    .sort((left, right) => left.layer - right.layer || left.index - right.index)
    .map(({ stroke }) => stroke);
}

export function drawingLayerMetadata(layers: DrawingLayer[], strokes: Stroke[], activeId: string): DrawingLayerMetadata[] {
  const counts = new Map<string, number>();
  for (const stroke of strokes) {
    const id = strokeDrawingLayerId(stroke);
    counts.set(id, (counts.get(id) ?? 0) + 1);
  }
  return layers.map((layer) => ({
    ...layer,
    drawingCount: counts.get(layer.id) ?? 0,
    active: layer.id === activeId,
  }));
}

export function isCatalogueDataLayer(layer: { provenance?: DataLayerProvenance }): boolean {
  return Boolean(layer.provenance && isLibraryAssetId(layer.provenance.assetId));
}

export function exceedsFreeDataLayerFeatures(layer: { provenance?: DataLayerProvenance }, count: number, previousCount = 0): boolean {
  return !isCatalogueDataLayer(layer) && count > Math.max(FREE_DATA_LAYER_FEATURE_LIMIT, previousCount);
}

export function exceedsFreeDataLayers(next: DataLayerPayload[], previous: DataLayerPayload[]): boolean {
  return next.length > Math.max(FREE_DATA_LAYER_LIMIT, previous.length) || next.some((item) =>
    exceedsFreeDataLayerFeatures(item.layer, item.features.length, previous.find((old) => old.layer.id === item.layer.id)?.features.length));
}

export function canPersistFreeDataLayers(layers: Array<{ persisted?: boolean; featureCount: number; provenance?: DataLayerProvenance }>): boolean {
  const existing = layers.filter((layer) => layer.persisted);
  const added = layers.filter((layer) => !layer.persisted);
  if (!added.length) return true;
  return existing.length + added.length <= FREE_DATA_LAYER_LIMIT
    && added.every((layer) => !exceedsFreeDataLayerFeatures(layer, layer.featureCount));
}

export function isStrokeEditable(stroke: Pick<Stroke, 'layerId'>, layers: DrawingLayer[]): boolean {
  return isStrokeVisible(stroke, layers) && !layers.find(layer => layer.id === strokeDrawingLayerId(stroke))?.locked;
}
